import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from 'sonner';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Login from './pages/Login';
import Join from './pages/Join';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Jobs from './pages/Jobs';
import Resources from './pages/Resources';
import Referrals from './pages/Referrals';
import Community from './pages/Community';
import Members from './pages/Members';
import Inbox from './pages/Inbox';
import ResourceHub from './pages/ResourceHub';
import DispatcherProfile from './pages/DispatcherProfile';
import ExternalJobBoards from './pages/ExternalJobBoards';
import Settings from './pages/Settings';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Verification from './pages/Verification';
import AdminDashboard from './pages/AdminDashboard';
import Subscription from './pages/Subscription';
import HelpCenter from './pages/HelpCenter';
import ResetPassword from './pages/ResetPassword';
import ComplianceDashboard from './pages/ComplianceDashboard';
import About from './pages/About';
import AffiliateHub from './pages/AffiliateHub';
import AffiliateLogin from './pages/AffiliateLogin';
import AffiliateJoin from './pages/AffiliateJoin';
import '@/App.css';

function App() {
  // Load theme preference on mount
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const { default: api } = await import('./utils/api');
        const res = await api.get('/profile/settings');
        const theme = res.data?.appearance?.theme;
        document.documentElement.classList.remove('dark');
        if (theme === 'dark') document.documentElement.classList.add('dark');
        else if (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches) document.documentElement.classList.add('dark');
      } catch { /* not logged in or no settings - use light */ }
    };
    loadTheme();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            duration={4000}
          />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/join" element={<Join />} />
            <Route path="/resource-hub" element={<ResourceHub />} />
            <Route path="/dispatcher/:id" element={<DispatcherProfile />} />
            <Route path="/dispatcher" element={<DispatcherProfile />} />
            <Route path="/dispatcher-profile" element={<DispatcherProfile />} />
            <Route path="/external-job-boards" element={<ExternalJobBoards />} />
            <Route path="/job-boards" element={<ExternalJobBoards />} />
            <Route path="/about" element={<About />} />
            <Route path="/affiliate-hub" element={<AffiliateHub />} />
            <Route path="/affiliate/login" element={<AffiliateLogin />} />
            <Route path="/affiliate/join" element={<AffiliateJoin />} />

            {/* Protected Routes */}
            <Route
              path="/verification"
              element={
                <ProtectedRoute>
                  <Verification />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs"
              element={
                <ProtectedRoute>
                  <Jobs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/resources"
              element={
                <ProtectedRoute>
                  <Resources />
                </ProtectedRoute>
              }
            />
            <Route
              path="/referrals"
              element={
                <ProtectedRoute>
                  <Referrals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/community"
              element={
                <ProtectedRoute>
                  <Community />
                </ProtectedRoute>
              }
            />
            <Route
              path="/members"
              element={
                <ProtectedRoute>
                  <Members />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inbox"
              element={
                <ProtectedRoute>
                  <Inbox />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription"
              element={
                <ProtectedRoute>
                  <Subscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="/help-center"
              element={
                <ProtectedRoute>
                  <HelpCenter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/compliance"
              element={
                <ProtectedRoute>
                  <ComplianceDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
          </Routes>
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;